import { useLazyQuery } from "@apollo/client";
import {
  CommunicationQuery,
  CommunicationStatus,
  CreateCommunicationInput,
  DocumentQuery,
  DocumentType,
  Issue,
} from "generated-graphql/graphql";
import {
  useValidatingForm,
  type UseValidatingFormReturn,
} from "hooks/useValidatingForm";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { COMMUNICATION_VALIDATOR_QUERY } from "../../api";
import { RecipientType } from "../../utils";
import {
  Communication,
  CommunicationFormType,
  RecipientFormType,
} from "../types";

export const DEFAULT_EMAIL_FROM = "support@encamp.com";
export const defaultRecipientForm: () => RecipientFormType = () => ({
  rowId: crypto.randomUUID(),
  lepc: null,
  coverLetterDocument: null,
  fireDepartment: null,
  policeDepartment: null,
  leadAgency: null,
  recipientType: RecipientType.CUSTOM,
  recipientName: "",
  communicationType: null,
  emailRecipientAddresses: [],
  emailSubject: "",
  emailReplyTo: "",
  emailTemplateName: "",
  emailBodyEnriched: {
    htmlTemplate: "",
    htmlHydrated: "",
  },
  mailRecipientAddressLine1: "",
  mailRecipientAddressLine2: "",
  mailRecipientCity: "",
  mailRecipientState: "",
  mailRecipientZip: "",
});

function getRecipientType(communication: CommunicationQuery["communication"]) {
  if (communication?.lepcId) {
    return RecipientType.LEPC;
  }
  if (communication?.fireDepartmentId) {
    return RecipientType.FIRE_DEPARTMENT;
  }
  if (communication?.policeDepartmentId) {
    return RecipientType.POLICE_DEPARTMENT;
  }
  if (communication?.leadAgencyId) {
    return RecipientType.LEAD_AGENCY;
  }
  return RecipientType.CUSTOM;
}

type CommunicationDocuments = NonNullable<
  CommunicationQuery["communication"]
>["communicationDocuments"];

function separateCoverLetterFromDocuments(
  communicationDocuments: CommunicationDocuments | null
): {
  coverLetter: CommunicationDocuments[number]["document"] | null;
  restOfDocuments: CommunicationDocuments[number]["document"][];
} {
  if (!communicationDocuments || communicationDocuments.length === 0) {
    return { coverLetter: null, restOfDocuments: [] };
  }

  const allSequencesOrInfinity = communicationDocuments.map(
    (d) => d.sequence ?? Infinity
  );
  const minIndex = Math.min(...allSequencesOrInfinity);

  // If the document with the lowest sequence is not a cover letter, then we
  // can't use it as the cover letter, so we return null for the cover letter
  // and the rest of the documents.
  if (
    minIndex === Infinity ||
    communicationDocuments[minIndex]?.document.documentType !==
      DocumentType.CoverLetter
  ) {
    return {
      coverLetter: null,
      restOfDocuments: communicationDocuments.map((cd) => cd.document),
    };
  }

  // At this point, minIndex is defined, so we can use it to get the cover letter
  const coverLetter = communicationDocuments[minIndex]?.document;
  const restOfDocuments = communicationDocuments
    .filter((cd) => cd.document.id !== coverLetter?.id)
    .map((cd) => cd.document);

  return { coverLetter, restOfDocuments };
}
const formDefaults: CommunicationFormType = {
  id: null,
  tenant: null,
  facilities: [],
  communicationDocuments: [],
  tierIIReports: [],
  status: CommunicationStatus.Pending,
  recipients: [defaultRecipientForm()],
};

export const useCommunicationForm = (
  existingCommunication?: Communication | null | undefined
) => {
  const { coverLetter, restOfDocuments } = useMemo(
    () =>
      separateCoverLetterFromDocuments(
        existingCommunication?.communicationDocuments ?? null
      ),
    [existingCommunication?.communicationDocuments]
  );

  const defaultValues = useMemo<CommunicationFormType>(() => {
    if (existingCommunication) {
      return {
        id: existingCommunication.id,
        status: existingCommunication.status,
        tenant: existingCommunication.tenant,
        facilities: existingCommunication.communicationFacilities.map(
          (cf) => cf.facility
        ),
        tierIIReports: existingCommunication.tierIIReports,
        communicationDocuments: restOfDocuments,
        recipients: [
          {
            rowId: crypto.randomUUID(),
            coverLetterDocument: coverLetter ?? null,
            recipientType: getRecipientType(existingCommunication),
            lepc: existingCommunication.lepc ?? null,
            lepcId: existingCommunication.lepcId,
            fireDepartment: existingCommunication.fireDepartment ?? null,
            fireDepartmentId: existingCommunication.fireDepartmentId,
            policeDepartment: existingCommunication.policeDepartment ?? null,
            policeDepartmentId: existingCommunication.policeDepartmentId,
            leadAgency: existingCommunication.leadAgency ?? null,
            leadAgencyId: existingCommunication.leadAgencyId,
            recipientName: existingCommunication.recipientName,
            communicationType: existingCommunication.communicationType,
            emailRecipientAddresses:
              existingCommunication.emailRecipientAddresses,
            emailSubject: existingCommunication.emailSubject,
            emailReplyTo: existingCommunication.emailReplyTo,
            emailTemplateName: existingCommunication.emailTemplateName,
            emailBodyEnriched: {
              // For an existing communication, we don't have the template,
              // and honestly it doesn't matter. Just show what we sent
              htmlTemplate: existingCommunication.emailBody ?? "",
              htmlHydrated: existingCommunication.emailBody ?? "",
            },
            mailRecipientAddressLine1:
              existingCommunication.mailRecipientAddressLine1,
            mailRecipientAddressLine2:
              existingCommunication.mailRecipientAddressLine2 ?? "",
            mailRecipientCity: existingCommunication.mailRecipientCity,
            mailRecipientState: existingCommunication.mailRecipientState,
            mailRecipientZip: existingCommunication.mailRecipientZip,
          },
        ],
      };
    }
    return formDefaults;
  }, [existingCommunication, coverLetter, restOfDocuments]);

  const methods = useValidatingForm<CommunicationFormType>(
    defaultValues,
    [],
    useCommunicationValidator()
  );

  return methods;
};

const getMode = (
  communicationId: string | null,
  isDraft: boolean
): "create" | "edit" | "view" => {
  if (communicationId) {
    if (isDraft) {
      return "edit";
    } else {
      return "view";
    }
  }
  return "create";
};

export const useCommunicationFormContext = () => {
  const form =
    useFormContext<CommunicationFormType>() as UseValidatingFormReturn<CommunicationFormType>;
  const { watch } = form;

  const isDraft = useMemo(
    () => watch("status") === CommunicationStatus.Draft,
    [watch]
  );
  const mode = useMemo(() => getMode(watch("id"), isDraft), [watch, isDraft]);

  return {
    ...form,
    isDraft,
    mode,
  };
};

// transform form data to the input type
export const transformCommunicationFormDataToInput = (
  data: CommunicationFormType
): CreateCommunicationInput => {
  const { recipients, tenant } = data;

  return {
    facilities: data.facilities.map((facility) => ({
      facilityId: facility.id,
    })),
    tierIIReportIds: data.tierIIReports.map((report) => report.id),
    communicationDocuments: data.communicationDocuments.map(
      (document, index) => ({
        documentId: document.id,
        sequence: index,
      })
    ),
    tenantId: tenant?.id ?? "",
    recipients: recipients.map((recipient) => {
      const {
        recipientType,
        lepc,
        fireDepartment,
        policeDepartment,
        leadAgency,
        coverLetterDocument,
        rowId,
        emailBodyEnriched,
        ...recipientInput
      } = recipient;

      return {
        ...recipientInput,
        lepcId: lepc?.id,
        fireDepartmentId: fireDepartment?.id,
        policeDepartmentId: policeDepartment?.id,
        leadAgencyId: leadAgency?.id,
        coverLetterDocumentId: coverLetterDocument?.id,
        // Pick the hydrated one!
        emailBody: emailBodyEnriched?.htmlHydrated ?? "",
      };
    }),
  };
};

const communicationSharedKeys = [
  "facilityIds",
  "tierIIReportIds",
  "communicationDocuments",
  "tenantId",
];
export const useCommunicationValidator = () => {
  const [query] = useLazyQuery(COMMUNICATION_VALIDATOR_QUERY, {});

  return useCallback(
    async (formData: CommunicationFormType) => {
      const input = transformCommunicationFormDataToInput(formData);
      const { data: result, previousData } = await query({
        variables: { input },
      });

      const validationResults = result
        ? [...result.communicationValidator]
        : [...(previousData?.communicationValidator ?? [])];

      const allIssuesWithPrefixedKeys = validationResults.map((issue) => {
        // Don't prefix the shared keys, because they aren't recipient-specific
        if (communicationSharedKeys.includes(issue?.key ?? "")) {
          return issue;
        }
        return {
          ...issue,
          // prepend the recipient keys with "recipient" to match the form definition
          key: `recipients.${issue.modelId}.${issue.key}`,
        };
      });

      return dedupeIssues({
        issues: allIssuesWithPrefixedKeys,
      });
    },
    [query]
  );
};

/**
 * Typically messages are unique, but the UI will present the
 * tenant/facility/reports/documents combined, so we want to dedupe them
 */
function dedupeIssues<T extends Pick<Issue, "key" | "message">>({
  issues,
}: {
  issues: T[];
}) {
  const seenSharedMessages = new Set<string>();
  const getIssueKey = (issue: T) => `${issue.key}-${issue.message}`;

  return issues.filter((issue) => {
    const key = getIssueKey(issue);
    if (seenSharedMessages.has(key)) {
      return false;
    }
    seenSharedMessages.add(key);
    return true;
  });
}
